import { Injectable } from "@angular/core";

import { ModalTitles, ModalType } from "@shared/Constantes/modal.constantes";
import { File } from "@shared/Entities/General/file.entity";
import { ModalService } from "@shared/Services/modal.service";

import { DocumentFile } from "../Types/document-file";
import { DocumentFileGroup } from "../Types/document-file-group";

@Injectable({
  providedIn: "root",
})
export class UploadMultipleFilesService {
  public filesList: DocumentFile[];
  public currentFilesUploaded: DocumentFile[];
  public filesGroups: DocumentFileGroup[];
  private fileSizeMegaBytesLessThan: number;
  private requestFilesSizeMegaBytesLessThan: number;
  public fileSizeMegaBytesLessThanMsg: string;
  public requestFilesSizeMegaBytesLessThanMsg: string;
  public invalidRequestFiles: boolean;
  public base64IncrementNumber = 1.4;

  constructor(private modalService: ModalService) {
    this.filesList = [];
    this.currentFilesUploaded = [];
    this.filesGroups = [];
    this.fileSizeMegaBytesLessThan = 5;
    this.requestFilesSizeMegaBytesLessThan = 25;

    this.fileSizeMegaBytesLessThanMsg = `El tamaño máximo permitido por documento es de ${this.fileSizeMegaBytesLessThan} MB. Por favor reduce el 
    tamaño del archivo o elige otro`;

    this.requestFilesSizeMegaBytesLessThanMsg = `El tamaño máximo total permitido es ${this.requestFilesSizeMegaBytesLessThan} MB. Por favor reduce
    el tamaño de los archivos o escoge otros`;

    this.invalidRequestFiles = false;
  }

  public init(params: { filesList: DocumentFile[]; currentFilesUpload: DocumentFile[] }): void {
    const { filesList, currentFilesUpload } = params;
    this.filesList = filesList ?? [];
    this.currentFilesUploaded = currentFilesUpload ?? [];
  }

  public validateFilesSizeMegaBytes(
    files: DocumentFile[]
  ): [
    size: number,
    invalid: boolean,
    fileSizeMegaBytesLessThan: boolean,
    requestFilesSizeMegaBytesLessThan: boolean,
    currentFileSize: number,
    files: DocumentFile[],
    currentFile: DocumentFile
  ] {
    this.invalidRequestFiles = false;

    const invalidFiles: DocumentFile[] = files
      .filter((x) => this.bytesToMegaBytes(x?.file?.object?.size ?? 0) > this.fileSizeMegaBytesLessThan)
      .reverse();

    let currentFile: DocumentFile[] = files?.length ? files.slice(-1) : [];
    currentFile = invalidFiles?.length ? invalidFiles.slice(-1) : currentFile;
    const currentFileSize: number = this.getFilesSizeMegaBytes1X1(currentFile, 3);

    const isfileSizeMegaBytesLessThan: boolean = files.every(
      (x) => this.bytesToMegaBytes(x?.file?.object?.size ?? 0) <= this.fileSizeMegaBytesLessThan
    );

    const size: number = this.getFilesSizeMegaBytes1X1(files, 3);
    const isRequestFilesSizeMegaBytesLessThan: boolean = size <= this.requestFilesSizeMegaBytesLessThan;
    const invalid: boolean = !isfileSizeMegaBytesLessThan || !isRequestFilesSizeMegaBytesLessThan;
    this.invalidRequestFiles = invalid;

    const result: [number, boolean, boolean, boolean, number, DocumentFile[], DocumentFile] = [
      size,
      invalid,
      isfileSizeMegaBytesLessThan,
      isRequestFilesSizeMegaBytesLessThan,
      currentFileSize,
      files,
      currentFile.length ? currentFile[0] : null,
    ];

    return result;
  }

  public get getFileSizeMegaBytesLessThan(): number {
    return this.fileSizeMegaBytesLessThan;
  }

  public get getRequestFilesSizeMegaBytesLessThan(): number {
    return this.requestFilesSizeMegaBytesLessThan;
  }

  public filesSizeValidationsAlert(
    validations: [
      size: number,
      invalid: boolean,
      fileSizeMegaBytesLessThan: boolean,
      requestFilesSizeMegaBytesLessThan: boolean,
      currentFileSize: number,
      files: DocumentFile[],
      currentFile: DocumentFile
    ]
  ): void {
    const [, invalid, fileSizeMegaBytesLessThan, requestFilesSizeMegaBytesLessThan, , files, currentFile] = validations;

    if (!invalid) return;

    let currentFileArray: DocumentFile[] = currentFile ? [currentFile] : [];
    if (!requestFilesSizeMegaBytesLessThan) {
      currentFileArray = files
        .filter((x) => this.bytesToMegaBytes(x?.file?.object?.size ?? 0) > this.requestFilesSizeMegaBytesLessThan)
        .reverse();
      currentFileArray = currentFileArray.length ? currentFileArray.slice(-1) : [];
    }

    let currentSize: number = 0;
    let errorMsg: string = `Error con el tamaño de los archivos`;

    if (!fileSizeMegaBytesLessThan) errorMsg = this.fileSizeMegaBytesLessThanMsg;
    if (!requestFilesSizeMegaBytesLessThan) errorMsg = this.requestFilesSizeMegaBytesLessThanMsg;
    if (!fileSizeMegaBytesLessThan || !requestFilesSizeMegaBytesLessThan)
      currentSize = this.getFilesSizeMegaBytes1X1(currentFileArray, 3);

    if (currentSize > 0) errorMsg += `<br/><br/>Tamaño actual: ${currentSize} MBS`;

    this.modalService.showModalGeneral({
      title: ModalTitles.warning,
      body: errorMsg,
      type: ModalType.warning,
    });
  }

  public bytesToMegaBytes(bytes: number): number {
    const sizeMb = bytes / (1024 * 1024);
    return sizeMb * this.base64IncrementNumber;
  }

  public roundSizeValue(sizeValue: number, digits: number = 0): number {
    const size = sizeValue ?? 0;
    let sizeStr: string | null = size?.toString() ?? null;
    const isScientificNotation: boolean = sizeStr?.includes("e-") ?? false;

    if (isScientificNotation) {
      const NotationComp: string[] = sizeStr?.split("e-") ?? [];
      sizeStr = NotationComp[1] ? parseFloat(sizeStr).toFixed(parseInt(NotationComp[1])) : null;
    }

    const decimal: number = parseFloat(sizeStr ?? "0");
    const [, , fixedDigits]: [number, string[], number | null, number] = this.getFirstNumberGreaterThanZeroIndex(decimal);

    digits = fixedDigits ?? digits;
    const sizeRounded: number = parseFloat(size.toFixed(digits));

    return sizeRounded;
  }

  private getFirstNumberGreaterThanZeroIndex(decimal: number): [number, string[], number | null, number] {
    const sizeComp: string[] = decimal?.toString()?.split(".") ?? [];
    const decimals: string | null = sizeComp[1] ?? null;
    const decimalsList: string[] = decimals?.split("") ?? [];
    const firstNumberGreaterThanZeroIndex: number = decimalsList.findIndex((x) => x !== "0");
    const indexIncrement: number = 1;
    const fixedDigits: number | null =
      firstNumberGreaterThanZeroIndex >= 0 ? firstNumberGreaterThanZeroIndex + indexIncrement : null;

    return [firstNumberGreaterThanZeroIndex, decimalsList, fixedDigits, indexIncrement];
  }

  private sizeMegaBytes(sizeBytes: number, digits: number): number {
    let sizeMb: number = this.bytesToMegaBytes(sizeBytes);

    const [, , fixedDigits]: [number, string[], number | null, number] = this.getFirstNumberGreaterThanZeroIndex(sizeMb);

    if (!digits || isNaN(digits)) digits = 0;
    digits = fixedDigits !== null && fixedDigits > digits ? fixedDigits : digits;

    const multiplicator: number = Math.pow(10, digits);
    sizeMb = parseFloat((sizeMb * multiplicator).toFixed(11));
    const test: number = Math.round(sizeMb) / multiplicator;

    return this.roundSizeValue(test, digits);
  }

  public getFileSizeMegaBytes(file: File, digits: number): number {
    return this.sizeMegaBytes(file.object.size, digits);
  }

  public getFilesSizeMegaBytes1X1(files: DocumentFile[], digits: number): number {
    let sizeMbs: number = 0;

    for (const file of files) {
      sizeMbs += this.sizeMegaBytes(file?.file?.object?.size ?? 0, digits);
    }

    return this.roundSizeValue(sizeMbs);
  }

  public getSizeMegaBytes(bytes: number, digits: number): number {
    return this.sizeMegaBytes(bytes, digits);
  }

  public groupFilesByReferenceName(files: DocumentFile[]): DocumentFileGroup[] {
    const filesGroups: DocumentFileGroup[] = [];

    files.forEach((file) => {
      const index = filesGroups.findIndex((fileGroup) => fileGroup.name === file.name);
      if (index >= 0) {
        filesGroups[index].files.push(file);
      } else {
        const newFileGroup: DocumentFileGroup = { name: file.name, files: [] };
        newFileGroup.files.push(file);
        filesGroups.push(newFileGroup);
      }
    });

    return filesGroups;
  }

  public getFilesAll(): DocumentFile[] {
    return this.filesList.concat(this.currentFilesUploaded);
  }

  public getFilesByReference(reference: string): DocumentFile[] {
    return this.getFilesAll().filter((x) => x.name === reference);
  }

  private filesSizeBytes(files: DocumentFile[]): number {
    let sizeBytes: number = 0;

    files.forEach((file) => {
      sizeBytes += file?.file?.object?.size ?? 0;
    });

    return sizeBytes;
  }

  private filesSizeMegaBytes(files: DocumentFile[]): number {
    return this.bytesToMegaBytes(this.filesSizeBytes(files));
  }

  public getFilesSizesBytesByReference(reference: string): number {
    const files: DocumentFile[] = this.getFilesAll().filter((x) => x.name === reference);
    return this.filesSizeBytes(files);
  }

  public getFilesSizesMegaBytesByReference(reference: string): number {
    const files: DocumentFile[] = this.getFilesAll().filter((x) => x.name === reference);
    return this.filesSizeMegaBytes(files);
  }

  public getFilesCountByReference(reference: string): number {
    return this.getFilesByReference(reference).length;
  }
}
